// پوسته اپ کارکنان (IA §2.1): Header (مرکز، شعبه فعال، حساب)، Sidebar مبتنی بر دسترسی، نوار حالت دسترسی و Breadcrumb.
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap } from '@phoenix/api-client';
import {
  AccountMenu,
  BranchSelector,
  FeatureDisabledNotice,
  useAccess,
  useTenantName,
  useTenantSession,
} from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  AccessModeBanner,
  AppShell,
  DEFAULT_TIME_ZONE,
  DisplaySettingsProvider,
  resolveMenu,
  type Crumb,
  type DisplaySettings,
  type ShellShortcut,
} from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Link as RouterLink, Outlet, useMatches } from 'react-router';
import { staffMenu, SUBSCRIPTION_PATH } from '../navigation';
import { CriticalNotificationBanner, NotificationBell } from '../pages/messaging/NotificationCenter';
import { PatientHeaderActions } from '../pages/patients/PatientQuickActions';
import { PLT_DISPLAY_SETTINGS_GROUP, pltKeys } from '../pages/settings/common';
import { ConnectionIndicator } from '../offline/ConnectionIndicator';
import { OfflineProvider } from '../offline/OfflineProvider';
import { AnnouncementBanner } from './AnnouncementBanner';
import { LicenseBanner } from './LicenseBanner';
import { SupportMenu } from './SupportMenu';

export interface RouteHandle {
  crumbs?: readonly Crumb[];
}

/**
 * واحد نمایش پول و منطقه زمانی مستأجر (plt.display_currency_unit، plt.default_timezone): پایه از me/access برای همه اعضا
 * (C-AGENT-185)؛ دارنده cfg.settings.view همان مقدار را از CFG می‌خواند تا ذخیره SCR-STAFF-906 بی‌درنگ اعمال شود
 * (C-AGENT-206، C-AGENT-224).
 */
function useTenantDisplaySettings(): DisplaySettings {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const query = useQuery({
    queryKey: pltKeys.settings(session?.tenantId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/configuration/settings', {
          params: { query: { group: PLT_DISPLAY_SETTINGS_GROUP } },
        }),
      ),
    enabled: access.can('cfg.settings.view'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const base = access.display;
  return useMemo(() => {
    const items = query.data?.items ?? [];
    const unit = items.find((item) => item.key === 'plt.display_currency_unit')?.value ?? base?.currencyUnit;
    const zone = items.find((item) => item.key === 'plt.default_timezone')?.value ?? base?.timeZone;
    return {
      currencyUnit: unit === 'Toman' ? 'Toman' : 'Rial',
      timeZone: typeof zone === 'string' && zone ? zone : DEFAULT_TIME_ZONE,
    };
  }, [query.data, base]);
}

/** «حساب من» در منوی حساب (SCR-STAFF-002). */
function AccountMenuItems({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('iam');
  return (
    <MenuItem component={RouterLink} to="/account" onClick={onClose} data-testid="account-page-link">
      <ListItemIcon>
        <ManageAccountsOutlinedIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primary={t('iam:account.menu')} />
    </MenuItem>
  );
}

export function StaffLayout() {
  const { t } = useTranslation('shell');
  const access = useAccess();
  const { activeTenant } = useTenantSession();
  const tenantName = useTenantName();
  const display = useTenantDisplaySettings();
  const matches = useMatches();

  const menu = useMemo(
    () => resolveMenu(staffMenu, { can: access.can, featureEnabled: access.featureEnabled }),
    [access.can, access.featureEnabled],
  );

  const crumbs = useMemo(() => {
    const last = [...matches].reverse().find((match) => (match.handle as RouteHandle | undefined)?.crumbs);
    return (last?.handle as RouteHandle | undefined)?.crumbs ?? [];
  }, [matches]);

  const shortcuts = useMemo<ShellShortcut[]>(
    () => [
      { keys: t('pat:quick.search_shortcut'), label: t('pat:quick.search') },
      { keys: t('pat:quick.new_patient_shortcut'), label: t('pat:quick.new_patient') },
      { keys: t('sch:quick.new_appointment_shortcut'), label: t('sch:quick.new_appointment') },
      { keys: t('sch:quick.walk_in_shortcut'), label: t('sch:quick.walk_in') },
    ],
    [t],
  );

  useEffect(() => {
    document.title = `${tenantName(activeTenant)} · ${t('shell:app.staff_name')}`;
  }, [t, tenantName, activeTenant]);

  return (
    <OfflineProvider>
    <DisplaySettingsProvider value={display}>
      <AppShell
        appNameKey="shell:app.staff_name"
        brand={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <LocalHospitalOutlinedIcon color="primary" />
            <Typography noWrap data-testid="header-tenant-name" sx={{ fontWeight: 600, maxWidth: 220 }}>
              {tenantName(activeTenant)}
            </Typography>
          </Stack>
        }
        headerContent={
          <Stack direction="row" spacing={2} alignItems="center">
            <BranchSelector />
            <PatientHeaderActions />
          </Stack>
        }
        headerActions={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ConnectionIndicator />
            <NotificationBell />
            <SupportMenu />
            <AccountMenu extraItems={(close) => <AccountMenuItems onClose={close} />} />
          </Stack>
        }
        menu={menu}
        shortcuts={shortcuts}
        upgradePath={SUBSCRIPTION_PATH}
        banner={
          <>
            <AccessModeBanner accessMode={access.accessMode} subscriptionPath={SUBSCRIPTION_PATH} />
            <LicenseBanner />
            <AnnouncementBanner />
            <CriticalNotificationBanner />
          </>
        }
        breadcrumbs={crumbs}
      >
        <Outlet />
      </AppShell>
      <FeatureDisabledNotice upgradePath={SUBSCRIPTION_PATH} />
    </DisplaySettingsProvider>
    </OfflineProvider>
  );
}
